import { useState, useEffect, useCallback } from 'react';
import { calculateProfileCompletion, meetsCompletionThreshold, getNextActions } from '../utils/profileCompletion';
import { fetchCompleteProfile } from '../../utils/api';
import { useAuth } from '../utils/AuthContext';

/**
 * Hook to track and manage profile completion
 * @param {number} requiredThreshold - Minimum completion percentage required (default: 100)
 * @returns {Object} Profile completion state and methods
 */
export const useProfileCompletion = (requiredThreshold = 100) => {
  const { user } = useAuth();
  const [completionData, setCompletionData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Calculate completion from profile data
  const calculateCompletion = useCallback((profileData) => {
    try {
      const completion = calculateProfileCompletion(profileData);
      const meetsThreshold = meetsCompletionThreshold(completion.totalPercentage, requiredThreshold);
      const nextActions = getNextActions(completion);

      return {
        ...completion,
        meetsThreshold,
        nextActions,
        requiredThreshold
      };
    } catch (err) {
      console.error('Error calculating profile completion:', err);
      return null;
    }
  }, [requiredThreshold]);

  // Load profile completion data
  const loadProfileCompletion = useCallback(async (force = false) => {
    if (!user?.id) {
      setCompletionData(null);
      setIsLoading(false);
      return;
    }

    // Don't reload if we have recent data and not forcing
    if (!force && completionData && lastUpdated && (Date.now() - lastUpdated < 30000)) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetchCompleteProfile(user.id);
      
      if (response.status === 'success' && response.data) {
        const completion = calculateCompletion(response.data);
        setCompletionData(completion);
        setLastUpdated(Date.now());
      } else {
        setError('Failed to load profile data');
        setCompletionData(null);
      }
    } catch (err) {
      console.error('Error loading profile completion:', err);
      setError(err.message || 'Failed to load profile completion');
      setCompletionData(null);
    } finally {
      setIsLoading(false);
    }
  }, [user?.id, completionData, lastUpdated, calculateCompletion]);

  // Update completion data with new profile data
  const updateCompletion = useCallback((newProfileData) => {
    if (newProfileData) {
      const completion = calculateCompletion(newProfileData);
      setCompletionData(completion);
      setLastUpdated(Date.now());
    }
  }, [calculateCompletion]);

  // Force refresh completion data
  const refreshCompletion = useCallback(() => {
    loadProfileCompletion(true);
  }, [loadProfileCompletion]);

  // Initial load
  useEffect(() => {
    loadProfileCompletion();
  }, [loadProfileCompletion]);

  // Computed values
  const isComplete = completionData?.isComplete || false;
  const percentage = completionData?.totalPercentage || 0;
  const meetsThreshold = completionData?.meetsThreshold || false;
  const nextActions = completionData?.nextActions || [];
  const missingFields = completionData?.missingFields || [];

  return {
    // State
    completionData,
    isLoading,
    error,
    lastUpdated,

    // Computed values
    isComplete,
    percentage,
    meetsThreshold,
    nextActions,
    missingFields,
    requiredThreshold,

    // Methods
    loadProfileCompletion,
    updateCompletion,
    refreshCompletion,

    // Helper methods
    canAccessOtherPages: meetsThreshold,
    shouldRedirectToProfile: !isLoading && !meetsThreshold,
    
    // Section completion details
    completedSections: completionData?.completedSections || {},
    missingSections: completionData?.missingSections || {}
  };
};

export default useProfileCompletion; 